import React, { useCallback, useRef, useState } from "react";
import { FigmaEditDialog } from "../components/FigmaEditDialog";
import { toFigmaEditCopyInput } from "../components/figmaEditCopy";
import { getDesignEditInfoGQL, PreparedDesignForEdit } from "../useDesignManagement";
import { FigmaEditTarget, useFigmaEditChoice } from "./useFigmaEditChoice";
import { withFreshEditInfo } from "./builderParams";

export interface FigmaAddressBoxDesign extends FigmaEditTarget {
  readonly hasFigmaPlaceholders?: boolean | null;
  readonly usedByCampaign?: boolean | null;
}

// A design restored from the campaign carries only id and format, so its source is looked up before choosing the path.
export const withDesignEditInfo = async <T extends { id: string; designSource?: string | null }>(
  design: T
): Promise<T> => {
  if (design.designSource !== undefined) return design;
  try {
    const info = await getDesignEditInfoGQL(design.id);
    if (!info) return design;
    return {
      ...design,
      designSource: info.designSource ?? null,
      hasFigmaPlaceholders: info.hasFigmaPlaceholders,
      usedByCampaign: info.usedByCampaign,
    };
  } catch (error) {
    console.warn(`[AddAddressBox] design lookup failed for ${design.id}, using the data on hand`, error);
    return design;
  }
};

interface UseFigmaAddressBoxDialogOptions {
  // Runs once the copy (or flattened original) has its rendition; the caller activates it and opens the builder.
  onPrepared: (prepared: PreparedDesignForEdit, design: FigmaAddressBoxDesign) => void;
  // Runs when the user leaves the dialog without a choice; nothing has been activated.
  onCancel?: () => void;
}

// "Add address box" on a Figma design asks copy or flatten first, with the same dialog as Edit (PAD-32395).
export const useFigmaAddressBoxDialog = ({ onPrepared, onCancel }: UseFigmaAddressBoxDialogOptions) => {
  const [design, setDesign] = useState<FigmaAddressBoxDesign | null>(null);
  // The prepare request outlives the render that started it, so the target is read from a ref.
  const designRef = useRef<FigmaAddressBoxDesign | null>(null);

  const handlePrepared = useCallback(
    (prepared: PreparedDesignForEdit) => {
      const target = designRef.current;
      designRef.current = null;
      setDesign(null);
      if (target) onPrepared(prepared, target);
    },
    [onPrepared]
  );

  const { busyChoice, showError, choose, reset } = useFigmaEditChoice({ onPrepared: handlePrepared });

  const openFigmaAddressBoxDialog = useCallback(
    async (target: FigmaAddressBoxDesign) => {
      reset();
      // The gallery row predates this campaign selecting (in ADM, activating) the design, so usage is re read.
      const info = await getDesignEditInfoGQL(target.id).catch((error) => {
        console.warn(`[AddAddressBox] usage lookup failed for ${target.id}, offering a copy only`, error);
        return null;
      });
      const fresh = withFreshEditInfo(target, info);
      designRef.current = fresh;
      setDesign(fresh);
    },
    [reset]
  );

  const close = useCallback(() => {
    designRef.current = null;
    setDesign(null);
    reset();
    onCancel?.();
  }, [reset, onCancel]);

  const figmaAddressBoxDialog = (
    <FigmaEditDialog
      open={Boolean(design)}
      {...toFigmaEditCopyInput(design ?? {})}
      busyChoice={busyChoice}
      showError={showError}
      onClose={close}
      onEditCopy={() => design && choose(design, "copy")}
      onFlattenOriginal={() => design && choose(design, "flatten")}
    />
  );

  return { openFigmaAddressBoxDialog, figmaAddressBoxDialog };
};
